import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, ZoomIn, ZoomOut, RotateCw, Check } from 'lucide-react';

// Header bg — wide banner output
const OUT_W = 1600;
const OUT_H = 320; // 5:1 aspect

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = 'anonymous';
    img.src = src;
  });
}

// Cropper ma dekhiyeko exact area rectangular banner ma convert garne
async function makeBannerFile(imageSrc, pixelCrop, rotation) {
  const image = await loadImage(imageSrc);

  const out = document.createElement('canvas');
  out.width = OUT_W;
  out.height = OUT_H;
  const octx = out.getContext('2d');

  // Crop rect ko center original image coords ma
  const pcx = pixelCrop.x + pixelCrop.width / 2;
  const pcy = pixelCrop.y + pixelCrop.height / 2;
  // Crop width -> banner width scale (zoom pixelCrop size ma already included)
  const s = OUT_W / Math.max(1, pixelCrop.width);

  octx.translate(OUT_W / 2, OUT_H / 2);
  octx.rotate((rotation * Math.PI) / 180);
  octx.scale(s, s);
  octx.translate(-pcx, -pcy);
  octx.drawImage(image, 0, 0);

  const blob = await new Promise((resolve) =>
    out.toBlob((b) => resolve(b), 'image/jpeg', 0.9)
  );
  if (!blob) throw new Error('Crop failed');
  return new File([blob], `header-bg-${Date.now()}.jpg`, { type: 'image/jpeg' });
}

function RectCropModal({ imageSrc, onCancel, onApply }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [applying, setApplying] = useState(false);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const applyCrop = async () => {
    if (!croppedAreaPixels) return;
    setApplying(true);
    try {
      const file = await makeBannerFile(imageSrc, croppedAreaPixels, rotation);
      onApply(file);
    } catch (e) {
      console.error('Error cropping image:', e);
      onCancel();
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h3 className="font-display text-lg font-bold">हेडर फोटो क्रप गर्नुहोस्</h3>
          <button type="button" onClick={onCancel} className="p-2 rounded-full hover:bg-paper-soft text-ink-soft hover:text-crimson transition-colors" aria-label="बन्द गर्नुहोस्">
            <X size={18} />
          </button>
        </div>

        <div className="relative w-full h-64 sm:h-72 bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={OUT_W / OUT_H}
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <ZoomOut size={16} className="text-muted flex-shrink-0" />
            <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-crimson" />
            <ZoomIn size={16} className="text-muted flex-shrink-0" />
          </div>
          <div className="flex items-center gap-2">
            <RotateCw size={15} className="text-muted flex-shrink-0" />
            <input type="range" min={0} max={360} step={1} value={rotation} onChange={(e) => setRotation(Number(e.target.value))} className="flex-1 accent-crimson" />
            <span className="text-[11.5px] text-muted w-9 text-right">{rotation}°</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line">
          <button type="button" onClick={onCancel} className="px-4 py-2.5 rounded-full border border-line text-[13px] font-semibold text-ink-soft hover:bg-paper-soft transition-colors">
            रद्द
          </button>
          <button type="button" onClick={applyCrop} disabled={applying} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-crimson text-white text-[13px] font-semibold hover:bg-crimson-dark transition-colors disabled:opacity-60">
            <Check size={15} /> {applying ? 'क्रप हुँदै...' : 'क्रप गर्नुहोस्'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RectCropModal;